// Hook que carga las sesiones 'completada' del paciente activo,
// ordenadas por ended_at desc (más reciente primero). Usado por
// HistorialSesiones.tsx.
//
// Filtros defensivos: ended_at IS NOT NULL (improbable que existan
// filas 'completada' con ended_at null porque el RPC lo setea siempre,
// pero si una row queda corrupta no rompe el orden).
//
// paciente_id resuelto vía useCurrentPacienteId() (string | null).
// Early return si null (loading / lookup-pending / huérfano).

import { useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentPacienteId } from "@/app/components/auth/AuthProvider";

export interface SesionHistoricaRow {
  id: string;
  ended_at: string;
  ejercicio_id: string;
  ejercicio_nombre: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
}

interface State {
  data: SesionHistoricaRow[] | null;
  error: string | null;
  loading: boolean;
}

/** Shape de la query: una sesión con su ejercicio joineado.
 *  Supabase devuelve el ejercicio anidado como objeto. */
interface SesionRowRaw {
  id: string;
  ended_at: string;
  ejercicio_id: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
  ejercicios: {
    nombre: string;
  } | null;
}

export function useHistorialSesiones(): State {
  const pacienteId = useCurrentPacienteId();
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    if (!pacienteId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("sesiones")
        .select(
          `
          id,
          ended_at,
          ejercicio_id,
          total_reps,
          score_promedio,
          duration_sec,
          ejercicios!inner(nombre)
        `,
        )
        .eq("paciente_id", pacienteId)
        .eq("estado", "completada")
        .not("ended_at", "is", null)
        .order("ended_at", { ascending: false })
        .returns<SesionRowRaw[]>();

      if (cancelled) return;

      if (error) {
        console.warn("[useHistorialSesiones] error cargando sesiones", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      const sesiones: SesionHistoricaRow[] = (data ?? []).map((row) => ({
        id: row.id,
        ended_at: row.ended_at,
        ejercicio_id: row.ejercicio_id,
        ejercicio_nombre: row.ejercicios?.nombre ?? row.ejercicio_id,
        total_reps: row.total_reps,
        score_promedio: row.score_promedio,
        duration_sec: row.duration_sec,
      }));

      setState({ data: sesiones, error: null, loading: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [pacienteId]);

  return state;
}
